import { useGetAiWorkersBySourceTypeQuery, RouteState, useDeleteActionMutation, useTypedSelector, useAppDispatch, updateActionsData, useEntityResourcePermissions } from "@circuitry-ai/doc-data";
import {
    AuditLogsAgGridWrapper,
    ACTIONS_AUDIT_LOG_STATIC_FILTERS,
    CardListViewMode,
    CustomTabs,
    EntityCardWithActions,
    EntityListView,
    ListActionsCellWithDelete,
    PageHeader,
    checkType,
    CtyButton,
    CtyShowToast,
    getActionsColumnDef,
    getDescriptionColumnDef,
    getNameColumnDef,
} from "@circuitry-ai/doc-ui";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ActionViewTabs } from "./actionview";
import { useEffect, useState, useMemo, useCallback } from "react";

function useDeleteActionHandler(action: any, refetchActionsList: () => void) {
    const [deleteAction, { isLoading: deleteActionLoading }] = useDeleteActionMutation();

    const handleConfirmDelete = useCallback(async (closeModal: () => void) => {
        try {
            const response = await deleteAction(action?.source_id).unwrap();
            CtyShowToast(checkType.success, response?.data);
            closeModal();
            refetchActionsList();
        } catch (error) {
            CtyShowToast(checkType.error, (error as Error)?.message || 'Delete Operation failed');
        }
    }, [action?.source_id, deleteAction, refetchActionsList]);

    return { handleConfirmDelete, isDeleteLoading: deleteActionLoading };
}

function useActionLaunchHandler(action: any) {
    const navigate = useNavigate();
    return useCallback(() => {
        if (action.source_id === '95df342e-3856-4cc0-87f4-d558f05604b2') {
            navigate('/formbuilder', { state: { action } });
        } else {
            navigate(`/actions/${RouteState.UPDATE}?tab=${ActionViewTabs.EXECUTE}`, { state: { action } });
        }
    }, [action, navigate]);
}

const ActionLaunchButton = ({ action, canRead }: { readonly action: any; readonly canRead: boolean }) => {
    const { t } = useTranslation();
    const handleLaunch = useActionLaunchHandler(action);

    return (
        <CtyButton className={'w-full border-1'} variant={'solid'} radius='sm' size={'lg'} color={'primary'} isDisabled={!canRead} onPress={handleLaunch}>
            {t('Launch')}
        </CtyButton>
    );
};

const ActionCardItem = ({ action, refetchActionsList }: { readonly action: any; readonly refetchActionsList: () => void }) => {
    const { t } = useTranslation();
    const actionResourceId = action?.source_id || action?.aiworker_id;
    const { canRead, canDelete } = useEntityResourcePermissions('action', actionResourceId);
    const { handleConfirmDelete, isDeleteLoading } = useDeleteActionHandler(action, refetchActionsList);

    return (
        <EntityCardWithActions
            id={action.aiworker_id}
            name={action.name}
            description={action.description}
            footerItems={<ActionLaunchButton action={action} canRead={canRead} />}
            canDelete={canDelete}
            dropdownAriaLabel="Action menu"
            deleteModalEntityName={action?.name ?? ''}
            onConfirmDelete={handleConfirmDelete}
            isDeleteLoading={isDeleteLoading}
            deleteConfirmLabel={t('Delete Action')}
        />
    );
};

const ActionActionsCell = ({ action, refetchActionsList }: { readonly action: any; readonly refetchActionsList: () => void }) => {
    const { t } = useTranslation();
    const actionResourceId = action?.source_id || action?.aiworker_id;
    const { canRead, canDelete } = useEntityResourcePermissions('action', actionResourceId);
    const { handleConfirmDelete, isDeleteLoading } = useDeleteActionHandler(action, refetchActionsList);
    const handleLaunch = useActionLaunchHandler(action);

    return (
        <ListActionsCellWithDelete
            actions={[
                {
                    label: t('Launch'),
                    onPress: handleLaunch,
                    variant: 'solid',
                    color: 'primary',
                    isDisabled: !canRead,
                },
            ]}
            canDelete={canDelete}
            entityName={action?.name ?? ''}
            onConfirmDelete={handleConfirmDelete}
            isDeleteLoading={isDeleteLoading}
            deleteConfirmLabel={t('Delete Action')}
        />
    );
};

const renderActionsActionsCell = (refetchActionsList: () => void) =>
    function ActionsActionsRenderer(params: any) {
        return <ActionActionsCell action={params?.data} refetchActionsList={refetchActionsList} />;
    };

export function ActionsPage() {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const token = useTypedSelector((state) => state.auth.token);
    const [skip, setSkip] = useState<boolean>(true);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [viewMode, setViewMode] = useState<CardListViewMode>(CardListViewMode.GRID);
    const [selectedTab, setSelectedTab] = useState('actions');
    const dispatch = useAppDispatch();

    useEffect(() => {
        if (token && skip) {
            setSkip(false);
        }
    }, [skip, token]);

    const { data: actionsList, isFetching, isLoading, error, refetch } = useGetAiWorkersBySourceTypeQuery('action', { skip, refetchOnMountOrArgChange: true });

    useEffect(() => {
        dispatch(updateActionsData(actionsList))
    }, [actionsList, dispatch]);

    const filteredActionsList = useMemo(() => {
        if (!actionsList || !searchQuery.trim()) {
            return actionsList ?? [];
        }
        return actionsList.filter((action: any) =>
            action.name?.toLowerCase().includes(searchQuery.toLowerCase())
        );
    }, [actionsList, searchQuery]);

    const columnDefs = useMemo(
        () => [
            getNameColumnDef(t('Name')),
            getDescriptionColumnDef(t('Description')),
            getActionsColumnDef({
                headerName: t('Actions'),
                minWidth: 200,
                maxWidth: 240,
                cellRenderer: renderActionsActionsCell(refetch),
            }),
        ],
        [t, refetch]
    );

    const renderActionsList = () => {
        if (actionsList?.length === 0 && !isLoading && !isFetching && !error) {
            return (
                <div className="flex-1 flex items-center justify-center h-full border-2 border-default-300 rounded-lg m-4 bg-default-100">
                    <div className="text-2xl text-primary">
                        {t('No Actions found')}
                    </div>
                </div>
            );
        }
        return (
            <EntityListView
                items={filteredActionsList}
                isLoading={isLoading || isFetching}
                error={error}
                searchId="actions-search"
                searchPlaceholder={t('Search actions')}
                onSearch={setSearchQuery}
                viewMode={viewMode}
                onViewModeChange={setViewMode}
                emptyMessage={t('No Actions found')}
                cardKey={(action: any) => action.aiworker_id}
                renderCard={(action: any) => (
                    <ActionCardItem action={action} refetchActionsList={refetch} />
                )}
                tableId="actions-list-table"
                columnDefs={columnDefs}
            />
        );
    };

    return (
        <div className='flex flex-col h-full min-h-0'>
            <PageHeader
                heading="Actions"
                description={'View all your Actions at one place.'}
                showCreateButton={selectedTab === 'actions'}
                createButtonText={'New Action'}
                entityType="action"
                onCreateButtonClick={() => navigate(`/actions/${RouteState.CREATE}?tab=${ActionViewTabs.CONFIGURATION}`)}
            />
            <div className="px-5">
            <CustomTabs
                tabs={[
                    { key: 'actions', title: t('Actions'), id: 'actions-tab' },
                    { key: 'audit-logs', title: t('Audit Logs'), id: 'actions-audit-logs-tab' },
                ]}
                selectedKey={selectedTab}
                onTabChange={setSelectedTab}
            />
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto">
                {selectedTab === 'actions' && renderActionsList()}
                {selectedTab === 'audit-logs' && (
                    <div className="pt-4 px-3">
                        <AuditLogsAgGridWrapper staticFilters={ACTIONS_AUDIT_LOG_STATIC_FILTERS} />
                    </div>
                )}
            </div>
        </div>
    );
}

export default ActionsPage;
